"use client";

import { useCallback, useEffect, useState, useRef } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  FileText,
  FolderKanban,
  ShoppingCart,
  Receipt,
  Package,
  Plus,
  Search,
  Home,
  Settings,
  Sparkles,
  Loader2,
  Calendar,
  Brain,
  Send,
} from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { getDolibarrClient } from "@/lib/dolibarr/client";
import { Badge } from "@/components/ui/badge";
import { buildPageContext, getAIConfig, selectProvider, estimateComplexity } from "@/services/aiService";

interface CommandMenuProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CommandMenu({ open: controlledOpen, onOpenChange }: CommandMenuProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [aiMode, setAiMode] = useState(false);
  const [aiResponse, setAiResponse] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const router = useRouter();
  const pathname = usePathname();
  const abortRef = useRef<AbortController | null>(null);

  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : open;
  const setIsOpen = isControlled ? onOpenChange! : setOpen;

  const client = getDolibarrClient();
  const pageContext = buildPageContext(pathname);

  // Detect if search looks like a question (IA mode)
  const looksLikeQuestion =
    search.length > 10 &&
    (/^(combien|quel|quelle|quels|quelles|liste|montre|affiche|résume|compare|analyse|cherche|trouve|top|ca |chiffre)/i.test(
      search
    ) ||
      search.includes("?") ||
      /\b(mois|semaine|année|en retard|impayé|marge|client|devis|facture)\b/i.test(search));

  // Search thirdparties
  const { data: thirdparties = [], isLoading: loadingThirdparties } = useQuery({
    queryKey: ["command-search-thirdparties", search],
    queryFn: async () => {
      if (!client || !search || search.length < 2 || aiMode) return [];
      return client.searchThirdParties(search);
    },
    enabled: isOpen && search.length >= 2 && !aiMode,
    staleTime: 30000,
  });

  // Search proposals
  const { data: proposals = [], isLoading: loadingProposals } = useQuery({
    queryKey: ["command-search-proposals", search],
    queryFn: async () => {
      if (!client || !search || search.length < 2 || aiMode) return [];
      const allProposals = await client.getProposals({ limit: 20 });
      return allProposals.filter(
        (p) =>
          p.ref?.toLowerCase().includes(search.toLowerCase()) ||
          p.ref_client?.toLowerCase().includes(search.toLowerCase())
      );
    },
    enabled: isOpen && search.length >= 2 && !aiMode,
    staleTime: 30000,
  });

  // Search projects
  const { data: projects = [], isLoading: loadingProjects } = useQuery({
    queryKey: ["command-search-projects", search],
    queryFn: async () => {
      if (!client || !search || search.length < 2 || aiMode) return [];
      const allProjects = await client.getProjects({ limit: 20 });
      return allProjects.filter(
        (p) =>
          p.ref?.toLowerCase().includes(search.toLowerCase()) ||
          p.title?.toLowerCase().includes(search.toLowerCase())
      );
    },
    enabled: isOpen && search.length >= 2 && !aiMode,
    staleTime: 30000,
  });

  // Search invoices
  const { data: invoices = [], isLoading: loadingInvoices } = useQuery({
    queryKey: ["command-search-invoices", search],
    queryFn: async () => {
      if (!client || !search || search.length < 2 || aiMode) return [];
      const allInvoices = await client.getInvoices({ limit: 20 });
      return allInvoices.filter((i) =>
        i.ref?.toLowerCase().includes(search.toLowerCase())
      );
    },
    enabled: isOpen && search.length >= 2 && !aiMode,
    staleTime: 30000,
  });

  // Search products
  const { data: products = [], isLoading: loadingProducts } = useQuery({
    queryKey: ["command-search-products", search],
    queryFn: async () => {
      if (!client || !search || search.length < 2 || aiMode) return [];
      const allProducts = await client.getProducts({ limit: 20, sqlfilters: `(t.ref:like:'%${search}%') OR (t.label:like:'%${search}%')` });
      return allProducts;
    },
    enabled: isOpen && search.length >= 2 && !aiMode,
    staleTime: 30000,
  });

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsOpen(!isOpen);
      }
      if (e.key === "n" && (e.metaKey || e.ctrlKey) && !e.shiftKey) {
        e.preventDefault();
        router.push("/devis/nouveau");
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [isOpen, setIsOpen]);

  // Clean up on close
  useEffect(() => {
    if (!isOpen) {
      setAiMode(false);
      setAiResponse("");
      setAiLoading(false);
      abortRef.current?.abort();
    }
  }, [isOpen]);

  const runCommand = useCallback(
    (command: () => void) => {
      setIsOpen(false);
      setSearch("");
      command();
    },
    [setIsOpen]
  );

  const askAI = useCallback(async () => {
    if (!search.trim()) return;
    setAiMode(true);
    setAiLoading(true);
    setAiResponse("");

    abortRef.current = new AbortController();

    try {
      const res = await fetch("/api/ai/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: [{ role: "user", content: search }],
          pageContext,
          provider: selectProvider(getAIConfig(), estimateComplexity(search)),
          stream: true,
        }),
        signal: abortRef.current.signal,
      });

      if (!res.ok) throw new Error("Erreur IA");

      const reader = res.body?.getReader();
      if (!reader) throw new Error("Pas de stream");

      const decoder = new TextDecoder();
      let full = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        for (const line of chunk.split("\n")) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (data === "[DONE]") continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.token) {
              full += parsed.token;
              setAiResponse(full);
            }
          } catch {
            // skip
          }
        }
      }
    } catch (err: any) {
      if (err.name !== "AbortError") {
        setAiResponse("Erreur lors de la requête IA. Vérifiez la configuration dans les paramètres.");
      }
    } finally {
      setAiLoading(false);
    }
  }, [search, pageContext]);

  const isSearching =
    loadingThirdparties || loadingProposals || loadingProjects || loadingInvoices || loadingProducts;

  const hasResults =
    thirdparties.length > 0 ||
    proposals.length > 0 ||
    projects.length > 0 ||
    invoices.length > 0 ||
    products.length > 0;

  return (
    <CommandDialog
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) setSearch("");
      }}
      title="Recherche rapide"
      description="Rechercher des pages, tiers, devis... ou poser une question IA"
    >
      <CommandInput
        placeholder={aiMode ? "Question IA..." : "Rechercher ou poser une question..."}
        value={search}
        onValueChange={(v) => {
          setSearch(v);
          if (aiMode && !aiLoading) {
            setAiMode(false);
            setAiResponse("");
          }
        }}
      />
      <CommandList>
        {/* AI Response mode */}
        {aiMode && (
          <div className="px-4 py-3">
            <div className="flex items-start gap-2 mb-2">
              <Brain className="h-4 w-4 text-indigo-500 mt-0.5 shrink-0" />
              <span className="text-xs font-medium text-indigo-500">Réponse IA</span>
            </div>
            {aiLoading && !aiResponse && (
              <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Analyse en cours...
              </div>
            )}
            {aiResponse && (
              <div className="text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed max-h-[300px] overflow-y-auto">
                {aiResponse}
                {aiLoading && <span className="animate-pulse">|</span>}
              </div>
            )}
          </div>
        )}

        {!aiMode && (
          <>
            <CommandEmpty>
              {isSearching ? (
                <div className="flex items-center justify-center gap-2 py-6 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Recherche en cours...</span>
                </div>
              ) : (
                "Aucun résultat trouvé."
              )}
            </CommandEmpty>

            {/* AI question suggestion */}
            {search.length >= 2 && looksLikeQuestion && (
              <CommandGroup heading="Intelligence Artificielle">
                <CommandItem onSelect={askAI}>
                  <Brain className="mr-2 h-4 w-4 text-indigo-500" />
                  <span className="flex-1">Demander à l&apos;IA : &quot;{search}&quot;</span>
                  <Send className="h-3.5 w-3.5 text-muted-foreground" />
                </CommandItem>
              </CommandGroup>
            )}

            {/* Search Results */}
            {search.length >= 2 && hasResults && (
              <>
                {thirdparties.length > 0 && (
                  <CommandGroup heading="Tiers">
                    {thirdparties.slice(0, 5).map((tp) => (
                      <CommandItem
                        key={tp.id}
                        onSelect={() =>
                          runCommand(() => router.push(`/tiers/${tp.id}`))
                        }
                      >
                        <Building2 className="mr-2 h-4 w-4" />
                        <div className="flex flex-col">
                          <span>{tp.name}</span>
                          {tp.email && (
                            <span className="text-xs text-muted-foreground">
                              {tp.email}
                            </span>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {proposals.length > 0 && (
                  <CommandGroup heading="Devis">
                    {proposals.slice(0, 5).map((p) => (
                      <CommandItem
                        key={p.id}
                        onSelect={() =>
                          runCommand(() => router.push(`/devis/${p.id}`))
                        }
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        <div className="flex items-center justify-between flex-1">
                          <span>{p.ref}</span>
                          <Badge
                            variant={
                              p.status === 2
                                ? "default"
                                : p.status === 1
                                ? "secondary"
                                : "outline"
                            }
                          >
                            {p.status === 0
                              ? "Brouillon"
                              : p.status === 1
                              ? "Validé"
                              : p.status === 2
                              ? "Signé"
                              : "Refusé"}
                          </Badge>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {projects.length > 0 && (
                  <CommandGroup heading="Chantiers">
                    {projects.slice(0, 5).map((proj) => (
                      <CommandItem
                        key={proj.id}
                        onSelect={() =>
                          runCommand(() => router.push(`/chantiers/${proj.id}`))
                        }
                      >
                        <FolderKanban className="mr-2 h-4 w-4" />
                        <span>{proj.ref || proj.title}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {invoices.length > 0 && (
                  <CommandGroup heading="Factures">
                    {invoices.slice(0, 5).map((inv) => (
                      <CommandItem
                        key={inv.id}
                        onSelect={() =>
                          runCommand(() => router.push(`/factures/${inv.id}`))
                        }
                      >
                        <Receipt className="mr-2 h-4 w-4" />
                        <div className="flex items-center justify-between flex-1">
                          <span>{inv.ref}</span>
                          {inv.paye === "1" ? (
                            <Badge variant="default">Payée</Badge>
                          ) : (
                            <Badge variant="destructive">Impayée</Badge>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {products.length > 0 && (
                  <CommandGroup heading="Produits">
                    {products.slice(0, 5).map((prod: any) => (
                      <CommandItem
                        key={prod.id}
                        onSelect={() =>
                          runCommand(() => router.push(`/produits/${prod.id}`))
                        }
                      >
                        <Package className="mr-2 h-4 w-4" />
                        <div className="flex items-center justify-between flex-1">
                          <span>{prod.ref} — {prod.label}</span>
                          {prod.price && (
                            <span className="text-xs text-muted-foreground">
                              {Number(prod.price).toFixed(2)} €
                            </span>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                <CommandSeparator />
              </>
            )}

            {/* Quick Actions */}
            {search.length < 2 && (
              <>
                <CommandGroup heading="Actions rapides">
                  <CommandItem
                    onSelect={() => runCommand(() => router.push("/devis/nouveau"))}
                  >
                    <Sparkles className="mr-2 h-4 w-4 text-blue-500" />
                    <span>Nouveau devis avec IA</span>
                    <CommandShortcut>⌘N</CommandShortcut>
                  </CommandItem>
                  <CommandItem
                    onSelect={() => runCommand(() => router.push("/tiers/nouveau"))}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    <span>Nouveau client</span>
                  </CommandItem>
                  <CommandItem
                    onSelect={() =>
                      runCommand(() => router.push("/chantiers/nouveau"))
                    }
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    <span>Nouveau chantier</span>
                  </CommandItem>
                </CommandGroup>

                <CommandSeparator />

                <CommandGroup heading={`Questions IA — ${pageContext.page}`}>
                  {(pageContext.suggestions || []).slice(0, 4).map((suggestion) => (
                    <CommandItem
                      key={suggestion}
                      onSelect={() => {
                        setSearch(suggestion);
                      }}
                    >
                      <Brain className="mr-2 h-4 w-4 text-indigo-400" />
                      <span>{suggestion}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>

                <CommandSeparator />

                <CommandGroup heading="Navigation">
                  <CommandItem onSelect={() => runCommand(() => router.push("/"))}>
                    <Home className="mr-2 h-4 w-4" />
                    <span>Tableau de bord</span>
                    <CommandShortcut>⌘H</CommandShortcut>
                  </CommandItem>
                  <CommandItem
                    onSelect={() => runCommand(() => router.push("/tiers"))}
                  >
                    <Building2 className="mr-2 h-4 w-4" />
                    <span>Tiers</span>
                  </CommandItem>
                  <CommandItem
                    onSelect={() => runCommand(() => router.push("/devis"))}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    <span>Devis</span>
                  </CommandItem>
                  <CommandItem
                    onSelect={() => runCommand(() => router.push("/commandes"))}
                  >
                    <ShoppingCart className="mr-2 h-4 w-4" />
                    <span>Commandes</span>
                  </CommandItem>
                  <CommandItem
                    onSelect={() => runCommand(() => router.push("/factures"))}
                  >
                    <Receipt className="mr-2 h-4 w-4" />
                    <span>Factures</span>
                  </CommandItem>
                  <CommandItem
                    onSelect={() => runCommand(() => router.push("/chantiers"))}
                  >
                    <FolderKanban className="mr-2 h-4 w-4" />
                    <span>Chantiers</span>
                  </CommandItem>
                  <CommandItem
                    onSelect={() => runCommand(() => router.push("/calendrier"))}
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    <span>Calendrier</span>
                  </CommandItem>
                  <CommandItem
                    onSelect={() => runCommand(() => router.push("/produits"))}
                  >
                    <Package className="mr-2 h-4 w-4" />
                    <span>Produits</span>
                  </CommandItem>
                </CommandGroup>

                <CommandSeparator />

                <CommandGroup heading="Paramètres">
                  <CommandItem
                    onSelect={() => runCommand(() => router.push("/parametres"))}
                  >
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Paramètres</span>
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}

// Button to trigger command menu
export function CommandMenuTrigger() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-md border bg-background px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
      >
        <Search className="h-4 w-4" />
        <span className="hidden sm:inline-block">Rechercher...</span>
        <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
          <span className="text-xs">⌘</span>K
        </kbd>
      </button>
      <CommandMenu open={open} onOpenChange={setOpen} />
    </>
  );
}
